import { useCallback, useEffect, useState } from 'react';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { presenceIndex } from '../lib/offline/deliveryState';

/**
 * useLocalPresence — la lectura de presencia local, VIVA (F6.7b, fase 1).
 *
 * Consume `readLocalPresence()` de F6.7a (presence.js) sin tocarla: una
 * lectura batched de toda la bodega, tres sentencias fijas, jamás N por doc.
 *
 * El censo (a) del sprint encontró el patrón que este hook mata: las pantallas
 * capturaban la presencia UNA vez al montar y no volvían a preguntar, así que
 * cuando el material aterrizaba la pantalla seguía diciendo que no estaba. Aquí
 * la lectura se repite ante el MISMO evento post-commit que ya refresca las
 * listas (`studybuddy:local-material-ready`, emitido una sola vez por corrida
 * del downloader) — reacción a un hecho, cero timers, cero polling.
 *
 * Devuelve:
 *   - presenceById: Map<id, fila> (vacío hasta la primera lectura)
 *   - presenceRead: ¿ya terminó al menos una lectura? Las pantallas ESPERAN
 *     este true antes de decidir un aviso o un CTA — nada de avisos que
 *     aparecen y desaparecen porque todavía no habíamos preguntado.
 *   - refresh(): re-lectura explícita (tras un reintento manual, p. ej.)
 *
 * En web es inerte por construcción: IS_APP_BUILD corta antes del import
 * dinámico, presenceRead queda en true (no hay nada que esperar) y el mapa
 * vacío hace que todo caiga a los conteos de servidor de siempre.
 */
export function useLocalPresence() {
  const [state, setState] = useState(() => ({
    presenceById: new Map(),
    presenceRead: !IS_APP_BUILD,
  }));

  const read = useCallback(async () => {
    if (!IS_APP_BUILD) return;
    try {
      const m = await import('../lib/warehouse/presence');
      const rows = await m.readLocalPresence();
      setState({ presenceById: presenceIndex(rows), presenceRead: true });
    } catch (e) {
      // readLocalPresence ya se traga sus propios fallos y devuelve []; esto
      // cubre solo un import que no resuelve. Se marca leída con mapa vacío:
      // dejar presenceRead en false para siempre congelaría los avisos.
      setState({ presenceById: new Map(), presenceRead: true });
    }
  }, []);

  useEffect(() => {
    if (!IS_APP_BUILD) return undefined;
    let cancelled = false;
    const run = () => { if (!cancelled) read(); };
    run();
    window.addEventListener('studybuddy:local-material-ready', run);
    return () => {
      cancelled = true;
      window.removeEventListener('studybuddy:local-material-ready', run);
    };
  }, [read]);

  return { presenceById: state.presenceById, presenceRead: state.presenceRead, refresh: read };
}

export default useLocalPresence;
